/**
 * SignalActions — the Signal named primary actions (frontend-entity-coverage
 * §2 / §6): **triage** (`PATCH status` → emits `signal.triaged`, `11 §4.5`),
 * **convert to evidence** (`POST /api/evidence` with `source_signal_id`,
 * `02 §3.4`), and **promote to package** (`candidate_package_*`, `[PROPOSED]`).
 *
 * All writes ride the shared {@link useApiAction} (boxed worker session + cache
 * invalidation), mirroring the {@link SignalTriage} board. The control degrades:
 * once a signal is past the convertible stage the verbs disappear; a failed
 * write only raises the standard error toast and leaves the record untouched.
 *
 * `triage` advances the signal one step along the canonical pipeline
 * (`new → triaged → clustered → candidate_object`); the worker emits
 * `signal.triaged` when `status` becomes `triaged`.
 */
import { Icon } from "@/components/ui/icon";
import { useGo, useNavigation } from "@refinedev/core";

import type { Signal, SignalStatus } from "@aos/shared";
import { Button } from "@/components/ui/button";
import { humanize } from "@/lib/format";

import { useApiAction } from "../_shared/useApiAction";

/** Linear triage pipeline used to compute the next "triage" target. */
const PIPELINE: readonly SignalStatus[] = [
  "new",
  "triaged",
  "clustered",
  "candidate_object",
] as const;

function nextStatus(status: SignalStatus): SignalStatus | null {
  const idx = PIPELINE.indexOf(status);
  if (idx === -1 || idx >= PIPELINE.length - 1) return null;
  return PIPELINE[idx + 1] ?? null;
}

function isConvertible(status: SignalStatus): boolean {
  return (
    status !== "converted_to_evidence" &&
    status !== "converted_to_package_proposal" &&
    status !== "archived" &&
    status !== "ignored"
  );
}

export interface SignalActionsProps {
  signal: Signal;
}

/** Inline triage / convert / promote actions for a signal's Inspector header. */
export function SignalActions({ signal }: SignalActionsProps) {
  const go = useGo();
  const { createUrl } = useNavigation();
  const { run, loading } = useApiAction();

  const advanceTarget = nextStatus(signal.status);
  const convertible = isConvertible(signal.status);

  const triage = async () => {
    if (!advanceTarget) return;
    try {
      await run(`/signals/${encodeURIComponent(signal.id)}`, {
        method: "PATCH",
        body: { status: advanceTarget },
        invalidateResources: ["signals", "events"],
        successMessage: `Signal moved to ${humanize(advanceTarget)}.`,
      });
    } catch {
      /* useApiAction surfaced the toast; degrade silently. */
    }
  };

  const convertToEvidence = async () => {
    try {
      await run(`/evidence`, {
        method: "POST",
        body: {
          title: signal.title,
          evidence_type: signal.signal_type,
          body: signal.body ?? undefined,
          polarity: "supports",
          source_signal_id: signal.id,
          confidence_score: signal.confidence_score ?? undefined,
        },
        invalidateResources: ["signals", "evidence"],
        successMessage: "Signal converted to evidence.",
      });
    } catch {
      /* degrade silently. */
    }
  };

  const promoteToPackage = () => {
    // Promote → seed a new package from this signal's candidate fields. The
    // backend `candidate_package_*` linkage is [PROPOSED]; we degrade to opening
    // the package create form prefilled via query (a never-crashing teaser path).
    go({
      to: createUrl("packages"),
      query: {
        package_type: signal.candidate_package_type ?? undefined,
        source_signal_id: signal.id,
      },
    });
  };

  return (
    <>
      {advanceTarget ? (
        <Button
          size="sm"
          disabled={loading}
          onClick={() => void triage()}
          data-testid="signal-triage"
        >
          <Icon name="next" size={16} className="mr-2" aria-hidden />
          Triage to {humanize(advanceTarget)}
        </Button>
      ) : null}
      {convertible ? (
        <Button
          variant="outline"
          size="sm"
          disabled={loading}
          onClick={() => void convertToEvidence()}
          data-testid="signal-convert-evidence"
        >
          <Icon name="evidence" size={16} className="mr-2" aria-hidden />
          Convert to evidence
        </Button>
      ) : null}
      {convertible ? (
        <Button
          variant="outline"
          size="sm"
          onClick={promoteToPackage}
          data-testid="signal-promote-package"
        >
          <Icon name="packages" size={16} className="mr-2" aria-hidden />
          Promote to package
        </Button>
      ) : null}
    </>
  );
}

export default SignalActions;
